import {useEffect, useMemo, useRef, useState} from 'react';

import {RestError, showRestError} from 'core/errors';
import {InnerFilters, InnerFiltersElement} from 'types';

const SEARCH_DEBOUNCE_MS = 500;

interface AuditPage<T> {
    data: T[];
    totalPages: number;
    totalElements: number;
}

export function useAuditLog<T, P>(
    fetchPage: (params: P & {page: number; size: number}) => Promise<AuditPage<T>>,
    mapFilters: (filters: InnerFilters) => P,
    initialFilters: InnerFilters = {},
) {
    const [filters, setFilters] = useState(initialFilters);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [data, setData] = useState<AuditPage<T> | null>(null);
    const [pending, setPending] = useState(true);
    const params = useMemo(() => ({...mapFilters(filters), page, size}), [
        mapFilters,
        filters,
        page,
        size,
    ]);

    useEffect(() => {
        let active = true;
        setPending(true);
        const timer = window.setTimeout(() => {
            fetchPage(params)
                .then((result) => {
                    if (active) setData(result);
                })
                .catch((error) => {
                    if (active) {
                        setData(null);
                        showRestError(error as RestError);
                    }
                })
                .finally(() => {
                    if (active) setPending(false);
                });
        }, 200);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [fetchPage, params]);

    const changeFilters = (values: InnerFiltersElement[]) => {
        setFilters((current) => ({
            ...current,
            ...Object.fromEntries(values.map(({id, value}) => [id, value])),
        }));
        setPage(0);
    };
    const resetFilters = (keys: string[]) => {
        setFilters((current) =>
            Object.fromEntries(Object.entries(current).filter(([key]) => !keys.includes(key))),
        );
        setPage(0);
    };
    return {filters, page, size, data, pending, setPage, setSize, changeFilters, resetFilters};
}

interface CursorAuditPage<T> {
    data: T[];
    nextPageRef?: string | null;
}

export function useCursorAuditLog<T, P>(
    fetchPage: (
        params: P & {paging: {pageSize: number; pageRef?: string | null}},
    ) => Promise<CursorAuditPage<T>>,
    mapFilters: (filters: InnerFilters) => P,
    initialFilters: InnerFilters = {},
) {
    const [filters, setFilters] = useState(initialFilters);
    const [search, setSearch] = useState(
        typeof initialFilters.search === 'string' ? initialFilters.search : '',
    );
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [data, setData] = useState<T[]>([]);
    const [hasNext, setHasNext] = useState(false);
    const [pending, setPending] = useState(true);
    const pageRefs = useRef<Record<number, string | null>>({0: null});
    const mappedFilters = useMemo(() => mapFilters(filters), [mapFilters, filters]);
    const appliedSearch = typeof filters.search === 'string' ? filters.search : '';

    useEffect(() => {
        let active = true;
        const pageRef = page === 0 ? undefined : pageRefs.current[page];
        if (page > 0 && !pageRef) {
            setPage(0);
            return undefined;
        }
        setPending(true);
        const timer = window.setTimeout(() => {
            fetchPage({
                ...mappedFilters,
                paging: {
                    pageSize: size,
                    ...(pageRef ? {pageRef} : {}),
                },
            })
                .then((result) => {
                    if (!active) return;
                    pageRefs.current[page + 1] = result.nextPageRef || null;
                    setData(result.data || []);
                    setHasNext(!!result.nextPageRef);
                })
                .catch((error) => {
                    if (active) {
                        setData([]);
                        setHasNext(false);
                        showRestError(error as RestError);
                    }
                })
                .finally(() => {
                    if (active) setPending(false);
                });
        }, 200);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [fetchPage, mappedFilters, page, size]);

    useEffect(() => {
        if (search === appliedSearch) return undefined;
        const timer = window.setTimeout(() => {
            pageRefs.current = {0: null};
            setFilters((current) => ({...current, search}));
            setPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [search, appliedSearch]);

    const resetPageRefs = () => {
        pageRefs.current = {0: null};
    };
    const changeFilters = (values: InnerFiltersElement[]) => {
        resetPageRefs();
        setFilters((current) => ({
            ...current,
            ...Object.fromEntries(values.map(({id, value}) => [id, value])),
        }));
        setPage(0);
    };
    const resetFilters = (keys: string[]) => {
        resetPageRefs();
        setFilters((current) =>
            Object.fromEntries(Object.entries(current).filter(([key]) => !keys.includes(key))),
        );
        if (keys.includes('search')) setSearch('');
        setPage(0);
    };

    return {
        filters,
        search,
        page,
        size,
        data,
        hasNext,
        pending,
        setPage,
        setSize,
        changeFilters,
        changeSearch: setSearch,
        resetFilters,
    };
}
